import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AuthorizationService, CorporateEntityService, MessageService } from 'src/app/services';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatPaginator, MatSort, MatTableDataSource } from '@angular/material';

import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { CorporateEntityEditDialogComponent } from '../corporate-entity-edit-dialog/corporate-entity-edit-dialog.component';
import { CorporateEntitySearchResultModel } from '../../models';
import { SearchCriteriaModel } from 'src/app/models/search-criteria-model';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

const DEFAULT_SORT = 'CorporateEntityIdentifier';

@Component({
  selector: 'app-corporate-entity-search',
  templateUrl: './corporate-entity-search.component.html',
  styleUrls: ['./corporate-entity-search.component.css']
})
export class CorporateEntitySearchComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild(CdkVirtualScrollViewport)
  viewport: CdkVirtualScrollViewport;
  defaultPageSize = 30;
  loadingGrid = false;
  pagerSubscription: Subscription;
  addCorporateEntityFeature = 'UI::CorporateEntityAdd';
  editCorporateEntityFeature = 'UI::CorporateEntityEdit';
  deleteCorporateEntityFeature = 'UI::CorporateEntityDelete';
  searchFormGroup: FormGroup;
  dataSource: MatTableDataSource<CorporateEntitySearchResultModel>;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  displayedColumns: string[] = [];
  searchString: string;
  searchCriteria: SearchCriteriaModel;
  sortOrder = '';
  newSortOrder = DEFAULT_SORT;

  constructor(private fb: FormBuilder,
    private corporateEntityService: CorporateEntityService,
    private messageService: MessageService,
    private authorizationService: AuthorizationService,
    private editDialog: MatDialog,
    private deleteDialog: MatDialog) {
  }

  ngOnInit() {
    this.initFormGroup();
    this.setupColumns();
    this.initialSearchLoad();
  }

  ngAfterViewInit() {
    this.paginator.pageSize = this.defaultPageSize;
    this.pagerSubscription = this.paginator.page
      .pipe(
        tap(() => this.loadSearchPage())
      )
      .subscribe();

    this.sort.sortChange.subscribe(data => {
      if (data['active'] !== 'ActionEdit' && data['active'] !== 'ActionDelete') {
        if (data['direction'] === '') {
          this.newSortOrder = '';
        } else {
          this.newSortOrder = `${(data['direction'] === 'desc') ? '-' : ''}${data['active']}`;
        }
        this.loadSearchPage();
      }
    });
  }

  initFormGroup() {
    this.searchFormGroup = this.fb.group({
      searchCriteria: new FormControl(''),
    });
  }

  setupColumns() {
    this.displayedColumns = [
      'CorporateEntityIdentifier',
      'CorporateEntityName',
      'CorporateEntityNameAnonymous',
      'PeerGroupName',
      'PeerGroupIncludeFlag'
    ];
    if (this.hasDeleteCorporateEntityFeature()) {
      this.displayedColumns.unshift('ActionDelete');
    }
    if (this.hasEditCorporateEntityFeature()) {
      this.displayedColumns.unshift('ActionEdit');
    }
  }

  ngOnDestroy() {
    if (this.pagerSubscription) {
      this.pagerSubscription.unsubscribe();
    }
  }

  search() {
    this.paginator.pageIndex = 0;
    this.loadSearchPage();
  }

  reset() {
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = this.defaultPageSize;
    this.initFormGroup();
    this.initialSearchLoad();
  }

  initialSearchLoad() {
    this.sortOrder = DEFAULT_SORT;
    this.newSortOrder = DEFAULT_SORT;
    this.sort.active = DEFAULT_SORT;
    this.sort.direction = 'asc';
    this.sort._stateChanges.next();

    this.searchString = '';
    this.loadSearchPageHelper(this.searchString, this.defaultPageSize, this.paginator.pageIndex, this.newSortOrder);
  }

  loadSearchPage() {
    if (this.newSortOrder !== this.sortOrder) {
      this.paginator.pageIndex = 0;
    }
    this.searchString = this.searchFormGroup.controls['searchCriteria'].value;
    this.loadSearchPageHelper(this.searchString, this.paginator.pageSize,
      (this.paginator.pageIndex * this.paginator.pageSize), this.newSortOrder);
  }

  loadSearchPageHelper(criteria: string, limit: number, offset: number, sortOrder: string) {

    if (this.sortOrder !== sortOrder) {
      this.sortOrder = sortOrder;
    }

    this.loadingGrid = true;
    this.searchCriteria = new SearchCriteriaModel();
    this.searchCriteria.criteria = this.searchString;
    this.corporateEntityService.search(this.searchCriteria, limit, offset, sortOrder)
      .subscribe(data => {
        this.dataSource = new MatTableDataSource<CorporateEntitySearchResultModel>(data.Items);

        this.dataSource.sortingDataAccessor = (myDataSource, sortHeaderId) => {
          if (typeof myDataSource[sortHeaderId] === 'string') {
            return myDataSource[sortHeaderId].toLocaleLowerCase();
          } else {
            return myDataSource[sortHeaderId];
          }
        };

        setTimeout(() => {
          this.paginator.length = data.TotalItemCount;
          this.dataSource.sort = this.sort;
        });
        this.loadingGrid = false;
      }, errorResponse => {
        this.messageService.HandleServerError(errorResponse);
        this.loadingGrid = false;
      });
  }

  add() {
    this.openCorporateEntityEditDialog(null);
  }

  edit(element: CorporateEntitySearchResultModel) {
    this.openCorporateEntityEditDialog(element);
  }
  //;dshjko;ftglojit;ijl;ikjstg
  openCorporateEntityEditDialog(element: CorporateEntitySearchResultModel): void {
    const dialogRef = this.editDialog.open(CorporateEntityEditDialogComponent,
      {
        width: '600px',
        data: { corporateEntityIdentifier: element ? element.CorporateEntityIdentifier : null }
      });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.search();
      }
    });
  }

  openDeleteDialog(element: CorporateEntitySearchResultModel): void {
    const dialogRef = this.deleteDialog.open(ConfirmationDialogComponent,
      {
        width: '600px',
        data: {
          title: 'Confirm Delete', body: `Are you sure you want to delete Corporate Entity:
          ${element.CorporateEntityIdentifier} :: ${element.CorporateEntityName} :: ${element.CorporateEntityNameAnonymous} ?`
        }
      });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        this.corporateEntityService.delete(element.CorporateEntityIdentifier)
          .subscribe(data => {
            this.messageService.ShowDeletedSuccessfullyMessage();
            this.search();
          }, errorResponse => {
            this.messageService.HandleServerError(errorResponse);
          });
      }
    });
  }

  hasAddCorporateEntityFeature(): boolean {
    return this.authorizationService.hasFeature(this.addCorporateEntityFeature);
  }

  hasEditCorporateEntityFeature(): boolean {
    return this.authorizationService.hasFeature(this.editCorporateEntityFeature);
  }

  hasDeleteCorporateEntityFeature(): boolean {
    return this.authorizationService.hasFeature(this.deleteCorporateEntityFeature);
  }
}
